import getSymbolFromCurrency from "currency-symbol-map";
import sortBy from "lodash.sortby";
import { v4 } from "uuid";

import {
  generateDefaultUpsellCartTargetingExpression,
  generateDefaultUpsellProductRuleAction,
  generateInitialAudience,
  generateInitialPostPurchaseBlock,
} from "@intelligems/ig-utils/post-purchase-defaults";
import {
  generateInitialBlockStyles,
  generateInitialBlockVariables,
} from "@intelligems/ig-utils/checkout-block-templates";

import {
  AudienceFilterDto,
  CreateAudienceDto,
  ExpressionNodeQueryType,
} from "@intelligems/ig-types/dtos/audiences";
import {
  BaseIntegrationDto,
  IntegrationName,
} from "@intelligems/ig-types/dtos/integrations";
import {
  BlockTemplateDisplayName,
  BlockTemplateMap,
  BlockTypeName,
  CheckoutBlockDto,
  PostPurchaseUpsellCheckoutBlockDto,
  TemplateForBlockType,
} from "@intelligems/ig-types/dtos/checkout-blocks";
import { CartTargetingFe } from "@intelligems/ig-types/fe-only/cart-targeting";
import {
  CheckoutBlockDtoMap,
  CheckoutSectionStyle,
} from "@intelligems/ig-types/fe-only/checkout-blocks";
import {
  ConditionType,
  RateConditionType,
} from "@intelligems/ig-types/dtos/shipping-rate-conditions";
import { CreateCustomEventDto } from "@intelligems/ig-types/dtos/analytics-events/custom-events";
import {
  CreateExclusionGroupEntityFe,
  CreateExclusionGroupFe,
} from "@intelligems/ig-types/fe-only/exclusion-groups";
import { CreateExperienceAnalysis } from "@intelligems/ig-types/dtos/experience-analysis";
import { CreateExperienceIntegrationDto } from "@intelligems/ig-types/dtos/experience-integrations";
import { CreateExperienceKeyMetricDto } from "@intelligems/ig-types/dtos/experience-key-metrics";
import { CreateExperienceProductTargetingFeDto } from "@intelligems/ig-types/fe-only/experience-product-targeting";
import {
  DISCOUNT_MAX_UNITS,
  DiscountTierDto,
} from "@intelligems/ig-types/dtos/discount-tiers";
import {
  DiscountApplicationMethodType,
  DiscountApplicationType,
  DiscountUnitType,
  OfferDto,
} from "@intelligems/ig-types/dtos/offers";
import { ExperienceCategory } from "@intelligems/ig-types/dtos/experience-categories";
import {
  ExperienceFe,
  TabEnumFe,
  TabType,
} from "@intelligems/ig-types/fe-only/experiences";
import { ExperiencePageTargetingFe } from "@intelligems/ig-types/fe-only/page-targeting";
import { ExperienceType } from "@intelligems/ig-types/dtos/experiences";
import { ExpressionNodeFe } from "@intelligems/ig-types/fe-only/shared-expression-targeting";
import { MarketRateScope } from "@intelligems/ig-types/dtos/market-rate-scope";
import { RateGroupType } from "@intelligems/ig-types/dtos/shipping-rate-groups";
import { RateType } from "@intelligems/ig-types/dtos/shipping-rates";
import {
  RedirectDto,
  RedirectFilter,
} from "@intelligems/ig-types/dtos/redirects";
import { ShippingRateFe } from "@intelligems/ig-types/fe-only/shipping-rate";
import { ShippingRateGroupFe } from "@intelligems/ig-types/fe-only/shipping-rate-groups";
import { ShopifyCurrencyType } from "@intelligems/ig-types/schemas/shopify";
import { ShopifyTemplateTypeType } from "@intelligems/ig-types/dtos/shopify";
import {
  UpsellCompareAtPriceDisplay,
  UpsellDiscountType,
  UpsellProductRulesDto,
} from "@intelligems/ig-types/dtos/upsell-product-rules";
import {
  VariationFe,
  VariationFeProps,
} from "@intelligems/ig-types/fe-only/variations";

export const getDefaultExperienceName = (
  hasTestPricing: boolean,
  hasTestShipping: boolean,
  hasTestCampaign: boolean,
  title?: string
): string => {
  const pricing = "Pricing";
  const shipping = "Shipping";
  const campaign = "Campaign";
  const content = "Content";

  if (title) {
    if (!title.includes(pricing) && hasTestPricing) {
      return getDefaultExperienceName(
        hasTestPricing,
        hasTestShipping,
        hasTestCampaign,
        `${title} + ${pricing}`
      );
    } else if (!title.includes(shipping) && hasTestShipping) {
      return getDefaultExperienceName(
        hasTestPricing,
        hasTestShipping,
        hasTestCampaign,
        `${title} + ${shipping}`
      );
    } else if (!title.includes(campaign) && hasTestCampaign) {
      return getDefaultExperienceName(
        hasTestPricing,
        hasTestShipping,
        hasTestCampaign,
        `${title} + ${campaign}`
      );
    } else {
      return `${title} Test`;
    }
  } else {
    if (hasTestPricing) {
      return getDefaultExperienceName(
        hasTestPricing,
        hasTestShipping,
        hasTestCampaign,
        pricing
      );
    } else if (hasTestShipping) {
      return getDefaultExperienceName(
        hasTestPricing,
        hasTestShipping,
        hasTestCampaign,
        shipping
      );
    } else if (hasTestCampaign) {
      return getDefaultExperienceName(
        hasTestPricing,
        hasTestShipping,
        hasTestCampaign,
        campaign
      );
    } else {
      return getDefaultExperienceName(
        hasTestPricing,
        hasTestShipping,
        hasTestCampaign,
        content
      );
    }
  }
};

export const initialFrontendProps = (options: {
  name: string;
}): VariationFeProps => {
  return {
    label: options.name,
  };
};

export const initialAudienceState = (
  priceTestDefaultCurrency?: string
): CreateAudienceDto =>
  generateInitialAudience({
    id: v4(),
    defaultCurrency: priceTestDefaultCurrency,
  });

export const initialAudienceFiltersRolloutsGenerate = (
  expression: ExpressionNodeFe<ExpressionNodeQueryType>[]
): AudienceFilterDto => {
  return {
    id: v4(),
    priority: 0,
    filterType: "utm",
    action: null,
    excludeFromAnalytics: false,
    expressionType: "custom",
    expression,
  };
};

export const initialAudienceFilterState = (): AudienceFilterDto => {
  return {
    id: v4(),
    priority: 0,
    filterType: "utm",
    action: null,
    excludeFromAnalytics: false,
    expressionType: "common",
    expression: [
      {
        query: {
          key: "",
          value: "",
          filter: "equals",
          type: "utm",
        },
      },
    ],
  };
};

export const initialPageTargetingState = (
  experienceId?: string
): ExperiencePageTargetingFe => {
  return {
    id: v4(),
    value: "",
    order: 0,
    filterType: "url",
    experienceId,
  };
};

export const initialExperienceProductTargetingState = (
  experienceId?: string
): CreateExperienceProductTargetingFeDto => {
  return {
    id: v4(),
    experienceId,
    target: "productPage",
    expression: null,
  };
};

export const initialRedirectState = ({
  variation,
  group,
  isThemeTest = false,
  templateType,
  isTemplateTest = false,
  copyRedirect,
  redirectFilter,
  skip,
  templateSuffixes = [],
}: {
  variation: VariationFe;
  group: number;
  isThemeTest: boolean;
  templateType?: ShopifyTemplateTypeType | null;
  isTemplateTest?: boolean;
  copyRedirect?: RedirectDto;
  redirectFilter?: RedirectFilter;
  skip?: boolean;
  templateSuffixes?: string[];
}): RedirectDto => {
  // For template tests, set queryParams based on whether it's control or test variation
  let queryParams: Array<{ key: string; value: string }> = [];
  if (isTemplateTest && templateType) {
    if (templateSuffixes.length > 0) {
      // Test variation with suffixes
      queryParams = [
        { key: "view", value: `${templateType}.${templateSuffixes[0]}.json` },
      ];
    } else {
      // Control variation (empty templateSuffixes)
      queryParams = [{ key: "view", value: `${templateType}.json` }];
    }
  }

  return {
    id: v4(),
    group,
    originUrl: copyRedirect?.originUrl ?? "",
    queryParams: queryParams,
    destinationUrl: copyRedirect?.destinationUrl ?? "",
    skip: skip ?? (variation.isControl && !isTemplateTest && !isThemeTest),
    variationId: variation.id,
    isThemeTest,
    isTemplateTest:
      isTemplateTest !== undefined ? isTemplateTest : !!templateType,
    templateType: templateType,
    templateSuffixes: templateSuffixes,
    redirectOnce: copyRedirect?.redirectOnce ?? false,
    filter: copyRedirect?.filter ?? redirectFilter ?? "none",
    find: undefined,
    redirectType: copyRedirect?.redirectType ?? "fullReplacement",
  };
};

export const offerTestDiscountTiersVariationInitial = (
  existingTiers: DiscountTierDto[]
): DiscountTierDto[] => {
  return sortBy(
    existingTiers.map((tier) => {
      return {
        ...tier,
        id: v4(),
      };
    }),
    (_) => _.maximumUnits
  );
};

export const initialCheckoutBlockState = <T extends keyof BlockTemplateMap>(
  variationId: string,
  blockType: T,
  blockTemplateType: TemplateForBlockType<T>,
  index: number,
  section?: CheckoutSectionStyle | null
): CheckoutBlockDtoMap[T] => {
  return {
    id: v4(),
    variationId,
    name: `${BlockTemplateDisplayName[blockTemplateType]} ${index + 1}`,
    locationId: `ig-block-${index + 1}`,
    blockType,
    blockTemplate: blockTemplateType,
    styles: generateInitialBlockStyles(blockType, blockTemplateType, section),
    variables: generateInitialBlockVariables(blockType, blockTemplateType),
  } as CheckoutBlockDtoMap[T];
};

export const initialPostPurchaseBlockState = (
  variationId: string,
  index = 0
): PostPurchaseUpsellCheckoutBlockDto =>
  generateInitialPostPurchaseBlock({
    id: v4(),
    variationId,
    name: `${BlockTypeName["postPurchaseUpsell"]} ${index + 1}`,
  });

export const initialDiscountTierState = (
  discountApplicationType?: DiscountApplicationType,
  unitType?: DiscountUnitType
): DiscountTierDto[] => {
  const baseTier = {
    minimumUnits: 0,
    maximumUnits: 1,
    unitDiscount: 0,
    bxgyGetQuantity: 0,
    discountTitle: null,
    giftWithPurchaseTitle: null,
    isFreeShipping: discountApplicationType === "freeShipping",
    isGiftWithPurchase: false,
    giftWithPurchaseVariantIds: [],
    autoAddGiftWithPurchase: true,
  };

  if (discountApplicationType === "buyXGetY") {
    return [
      {
        id: v4(),
        ...baseTier,
        minimumUnits: 2,
        maximumUnits: DISCOUNT_MAX_UNITS,
        unitDiscount: 100,
        bxgyGetQuantity: 1,
        autoAddGiftWithPurchase: false,
      },
    ];
  }

  if (discountApplicationType === "tieredDiscount") {
    if (unitType === "dollar") {
      return [
        {
          id: v4(),
          ...baseTier,
          minimumUnits: 50,
          maximumUnits: 99,
        },
        {
          id: v4(),
          ...baseTier,
          minimumUnits: 100,
          maximumUnits: DISCOUNT_MAX_UNITS,
        },
      ];
    }
    return [
      {
        id: v4(),
        ...baseTier,
        minimumUnits: 2,
        maximumUnits: 3,
      },
      {
        id: v4(),
        ...baseTier,
        minimumUnits: 4,
        maximumUnits: DISCOUNT_MAX_UNITS,
      },
    ];
  }

  return [
    {
      id: v4(),
      ...baseTier,
    },
  ];
};

export const initialExperienceAnalysis = (options: {
  testFlowType: ExperienceType;
  useWideApp: boolean;
  defaultCurrency?: string;
  hasTestPricing: boolean;
  hasTestShipping: boolean;
  hasTestCampaign: boolean;
  hasTestContent: boolean;
  hasTestContentUrl: boolean;
  hasTestContentAdvanced: boolean;
  hasTestContentOnsite: boolean;
  hasTestContentTemplate: boolean;
  hasTestContentTheme: boolean;
  hasTestOnsiteInjections: boolean;
  isUpsell?: boolean;
}): CreateExperienceAnalysis => {
  return {
    analyticsViewType:
      options.hasTestPricing || options.isUpsell
        ? "test_orders_only"
        : options.hasTestShipping ||
            options.hasTestCampaign ||
            (!options.hasTestCampaign &&
              !options.hasTestShipping &&
              !options.hasTestPricing)
          ? "sitewide_orders"
          : "test_orders_only",
  };
};

const getExperienceIntegrations = (
  testFlowType: ExperienceType,
  integrations: BaseIntegrationDto[]
): CreateExperienceIntegrationDto[] => {
  const EXPERIENCE_INTEGRATIONS: readonly IntegrationName[] = [
    "StayAi",
    "Recharge",
  ];
  if (testFlowType !== "pricing") {
    return [];
  }
  return (
    integrations
      .filter((integration) =>
        EXPERIENCE_INTEGRATIONS.includes(integration.name)
      )
      .map((integration) => ({
        integration: {
          name: integration.name,
          description: integration.description,
          apis: integration.apis,
          globalSettings: integration.globalSettings,
          enabled: integration.enabled,
        },
        enabled: integration.name === "StayAi" ? false : integration.enabled,
        experienceSettings: {},
      })) ?? []
  );
};

// this is for experiment
export const initialExperienceFeDto = (options: {
  testFlowType: ExperienceType;
  useWideApp: boolean;
  defaultCurrency?: string;
  hasTestPricing: boolean;
  hasTestShipping: boolean;
  hasTestCampaign: boolean;
  hasTestContent: boolean;
  hasTestContentUrl: boolean;
  hasTestContentAdvanced: boolean;
  hasTestContentOnsite: boolean;
  hasTestContentTemplate: boolean;
  hasTestContentTheme: boolean;
  hasTestOnsiteInjections: boolean;
  hasTestCheckoutBlocks: boolean;
  hasTestPostPurchase: boolean;
  hasTestOnsiteUpsell: boolean;
  category?: ExperienceCategory;
  name?: string;
  description?: string;
  tab?: TabType;
  integrations?: BaseIntegrationDto[] | undefined;
  isUpsell?: boolean;
}): ExperienceFe => {
  return {
    id: undefined,
    isPreview: true,
    category: options.category ?? "experiment",
    viewMode: "edit",
    actionMode: "create",
    tab: options.tab
      ? options.tab
      : options.category === "experiment"
        ? TabEnumFe.TEST_GROUPS
        : TabEnumFe.MODS,
    productsLocation: null,
    name: options.name ?? "",
    description: options.description ?? "",
    status: "pending",
    experienceActions: [],
    isExternal: false,
    testTypes: {
      hasTestPricing: options.hasTestPricing,
      hasTestShipping: options.hasTestShipping,
      hasTestCampaign: options.hasTestCampaign,
      hasTestContent: options.hasTestContent,
      hasTestContentUrl: options.hasTestContentUrl,
      hasTestContentAdvanced: options.hasTestContentAdvanced,
      hasTestContentOnsite: options.hasTestContentOnsite,
      hasTestContentTemplate: options.hasTestContentTemplate,
      hasTestContentTheme: options.hasTestContentTheme,
      hasTestOnsiteInjections: options.hasTestOnsiteInjections,
      hasTestCheckoutBlocks: options.hasTestCheckoutBlocks,
      hasTestPostPurchase: options.hasTestPostPurchase,
      hasTestOnsiteUpsell: options.hasTestOnsiteUpsell,
    },
    variations: [],
    experiencePageTargeting: [],
    experienceProductTargeting: [],
    experienceIntegrations: options.integrations
      ? getExperienceIntegrations(options.testFlowType, options.integrations)
      : [],
    experienceAnalysis: initialExperienceAnalysis(options),
    experienceCustomMetrics: [],
    requiresLink: false,
    linkBaseUrl: null,
    shippingCarrierProvider: "intelligems",
    devicePreview: {
      device: "desktop",
      path: "/",
    },
    shippingTestMethodDefinitions: [],
    type: options.testFlowType,
    startedAtTs: null,
    endedAtTs: null,
    pausedAtTs: null,
    productVariantCount: 0,
    currency:
      options.hasTestPricing && options.defaultCurrency
        ? {
            code: options.defaultCurrency,
            symbol: getSymbolFromCurrency(options.defaultCurrency) || "$",
          }
        : null,
    isNew: true,
  };
};

export const offerTestOfferVariationInitial = ({
  existingOffer,
  variationId,
  id,
  isDraftOffer,
}: {
  existingOffer: OfferDto;
  variationId: string;
  id: string;
  isDraftOffer?: boolean;
}): OfferDto => {
  return {
    id,
    enabled: false,
    isTest: true,
    name: isDraftOffer
      ? existingOffer.name
      : `Discount Copy of ${existingOffer.name}`,
    unitType: existingOffer.unitType,
    discountType: existingOffer.discountType,
    discountApplicationType: existingOffer.discountApplicationType,
    discountApplicationMethod: existingOffer.discountApplicationMethod,
    tiers: offerTestDiscountTiersVariationInitial(existingOffer.tiers),
    isArchived: false,
    combinesWithIntelligems: existingOffer.combinesWithIntelligems,
    combinesWithShopify: existingOffer.combinesWithShopify,
    shouldRejectCodes: existingOffer.shouldRejectCodes,
    variationId,
    shippingDiscount: existingOffer.shippingDiscount
      ? { ...existingOffer.shippingDiscount, id: v4() }
      : null,
    requireSameItem: existingOffer.requireSameItem,
    bxgyGetProductScope: existingOffer.bxgyGetProductScope,
    subscriptionApplicationType: existingOffer.subscriptionApplicationType,
  };
};

export const checkoutBlockTestCopyInitial = ({
  checkoutBlock,
  variationId,
}: {
  checkoutBlock: CheckoutBlockDto;
  variationId: string;
}): CheckoutBlockDto => {
  return {
    ...checkoutBlock,
    variationId,
    id: v4(),
  };
};

export const initialOfferState = ({
  totalCount,
  isTest,
  variationId,
  discountApplicationType,
  discountApplicationMethod,
  unitType,
}: {
  totalCount?: number;
  isTest?: boolean;
  variationId: string;
  discountApplicationType?: DiscountApplicationType;
  discountApplicationMethod?: DiscountApplicationMethodType;
  unitType?: DiscountUnitType;
}): OfferDto => {
  return {
    id: v4(),
    enabled: false,
    isTest: isTest || false,
    name: totalCount ? `Discount ${totalCount + 1}` : `Discount`,
    unitType:
      discountApplicationType === "buyXGetY" ? "unit" : unitType || "unit",
    discountType: "percentage",
    discountApplicationType: discountApplicationType || "",
    discountApplicationMethod: discountApplicationMethod || "perItem",
    tiers: initialDiscountTierState(discountApplicationType, unitType),
    isArchived: false,
    combinesWithIntelligems: true,
    combinesWithShopify: true,
    shouldRejectCodes: false,
    variationId,
    shippingDiscount:
      discountApplicationType === "freeShipping"
        ? {
            id: v4(),
            discountType: "percentage",
            unitDiscount: 100,
            countryFilter: null,
            rateFilter: {
              type: "name",
              rules: [{ id: v4(), operator: "is", name: "" }],
            },
            discountLabel: null,
          }
        : null,
    requireSameItem: false,
    bxgyGetProductScope: null,
    // Free shipping always applies to both purchase types.
    subscriptionApplicationType:
      discountApplicationType === "freeShipping"
        ? "oneTimeAndFirstSub"
        : "oneTime",
  };
};

export const initialTierShippingState = (
  tierType: RateConditionType,
  unitType: ConditionType
) => {
  return {
    min: null,
    max: null,
    conditionType: tierType,
    unit: unitType,
  };
};

export const initialShippingRateState = (
  storeCurrency: ShopifyCurrencyType,
  rateType: RateType,
  selectedRateType: RateGroupType,
  tierType: RateConditionType,
  unitType: ConditionType
): ShippingRateFe => {
  return {
    name: "",
    amount: 0,
    rateType: rateType,
    currency: storeCurrency,
    condition:
      selectedRateType === "tieredByPriceOrWeight"
        ? initialTierShippingState(tierType, unitType)
        : undefined,
  };
};

export const initialShippingRateGroupsState = (
  selectedRateType: RateGroupType,
  storeCurrency: ShopifyCurrencyType,
  tierType: RateConditionType,
  unitType: ConditionType,
  rateGroupPosition: number,
  variationId: string,
  scopes: MarketRateScope[] | null = null
): ShippingRateGroupFe => {
  const types: RateType[] = [];

  if (selectedRateType === "flatRate") {
    types.push("amount");
  } else if (selectedRateType === "flatRateWithThreshold") {
    types.push("amount", "threshold");
  } else if (selectedRateType === "thresholdOnly") {
    types.push("threshold");
  } else {
    types.push("tier");
  }
  return {
    groupType: selectedRateType,
    position: rateGroupPosition,
    variationId,
    scopes,
    rates:
      selectedRateType === "custom"
        ? []
        : types.map((rate) =>
            initialShippingRateState(
              storeCurrency,
              rate,
              selectedRateType,
              tierType,
              unitType
            )
          ),
  };
};

export const initialSingleVariationState = (): VariationFe => {
  return {
    id: v4(),
    isControl: true,
    name: "Control Group",
    order: 0,
    percentage: 100,
    priceChange: undefined,
    priceChangeUnit: "percent",
    shippingRateGroups: [],
    excludeProducts: false,
    includeProducts: false,
    functionSyncError: false,
    ...initialFrontendProps({
      name: "Control Group",
    }),
  };
};

export const initialVariationState = (): VariationFe[] => {
  return [
    {
      id: v4(),
      isControl: true,
      name: "Control Group",
      order: 0,
      percentage: 50,
      priceChange: undefined,
      priceChangeUnit: "percent",
      shippingRateGroups: [],
      excludeProducts: false,
      includeProducts: false,
      functionSyncError: false,
      ...initialFrontendProps({
        name: "Control Group",
      }),
    },
    {
      id: v4(),
      isControl: false,
      name: "New Group 1",
      order: 1,
      percentage: 50,
      priceChange: undefined,
      functionSyncError: false,
      priceChangeUnit: "percent",
      excludeProducts: false,
      includeProducts: false,
      shippingRateGroups: [],
      ...initialFrontendProps({
        name: "New Group 1",
      }),
    },
  ];
};

export const initialVariation = (
  id: string,
  maxOrder: number,
  variations: VariationFe[]
): VariationFe => {
  let newName = `New Group ${maxOrder + 1}`;

  if (variations.some((variation) => variation.name === newName)) {
    newName = `New Group ${maxOrder + 2}`;
  }
  return {
    id,
    name: newName,
    isControl: false,
    order: maxOrder + 1,
    percentage: 100 / (variations.length + 1),
    priceChange: undefined,
    priceChangeUnit: "percent",
    shippingRateGroups: [],
    label: `New Group ${maxOrder + 1}`,
    priceRoundingType: "none",
    priceRoundingAmount: "0",
    excludeProducts: false,
    includeProducts: false,
    functionSyncError: false,
  };
};

export const initialExclusionGroupEntityStateFunc = (
  entityId?: string,
  order?: number
): CreateExclusionGroupEntityFe => {
  return {
    percentage: 0,
    order: order ?? 0,
    hasPriorityOrder: false,
    experienceId: entityId,
  };
};

export const initialExclusionGroupStateFunc = (
  entityId?: string
): CreateExclusionGroupFe => {
  return {
    name: "",
    exclusionGroupEntities: [initialExclusionGroupEntityStateFunc(entityId)],
    reassignOnEnd: true,
    hasWeightedValues: false,
  };
};

export const initPageViewEvent = (): CreateCustomEventDto => ({
  name: "",
  description: "",
  type: "pageView",
  identifier: "",
  settings: {},
  applyToUrls: [],
});

export const initProductPageViewEvent = (): CreateCustomEventDto => ({
  name: "",
  description: "",
  type: "productPageView",
  identifier: "",
  settings: [
    {
      productId: "",
      handle: "",
    },
  ],
});

export const initCollectionPageViewEvent = (): CreateCustomEventDto => ({
  name: "",
  description: "",
  type: "collectionPageView",
  identifier: "",
  settings: [
    {
      handle: "",
    },
  ],
});

export const initClickEvent = (): CreateCustomEventDto => ({
  name: "",
  description: "",
  type: "clickEvent",
  identifier: "",
  settings: {
    selectors: [],
  },
  applyToUrls: [],
});

export const initElementViewedEvent = (): CreateCustomEventDto => ({
  name: "",
  description: "",
  type: "elementViewed",
  identifier: "",
  settings: {
    selectors: [],
  },
  applyToUrls: [],
});

export const initScrollDepthEvent = (): CreateCustomEventDto => ({
  name: "",
  description: "",
  type: "scrollDepth",
  identifier: "",
  settings: {
    mobile: { percentage: 50 },
    desktop: { percentage: 50 },
  },
  applyToUrls: [],
});

export const initJavascriptEvent = (): CreateCustomEventDto => ({
  name: "",
  description: "",
  type: "javascriptEvent",
  identifier: "",
  settings: {
    code: "",
  },
  applyToUrls: [],
});

export const initJavascriptImportedEvent = (): CreateCustomEventDto => ({
  name: "",
  description: "",
  type: "javascriptImportedEvent",
  identifier: "",
  settings: {
    registered: false,
  },
});

export const initExperienceKeyMetrics = (
  shouldShowProfit: boolean,
  hasTestPostPurchase = false
): CreateExperienceKeyMetricDto[] => {
  const keyMetrics: CreateExperienceKeyMetricDto[] = [
    {
      order: 0,
      standardEventId: "conversionRate",
      isPrimary: false,
    },
    {
      order: 1,
      standardEventId: "netRevenuePerVisitor",
      isPrimary: !shouldShowProfit,
    },
    {
      order: 2,
      standardEventId: "profitPerVisitor",
      isPrimary: shouldShowProfit,
    },
    {
      order: 3,
      standardEventId: "netRevenuePerOrder",
      isPrimary: false,
    },
  ];

  if (hasTestPostPurchase) {
    keyMetrics.push(
      {
        order: keyMetrics.length,
        standardEventId: "pctOrdersWithPostPurchaseOffer",
        isPrimary: false,
      },
      {
        order: keyMetrics.length + 1,
        standardEventId: "netRevenueFromPostPurchaseOffer",
        isPrimary: false,
      }
    );
  }

  return keyMetrics;
};

export const initialCustomEventStates = {
  pageView: initPageViewEvent(),
  productPageView: initProductPageViewEvent(),
  collectionPageView: initCollectionPageViewEvent(),
  clickEvent: initClickEvent(),
  elementViewed: initElementViewedEvent(),
  scrollDepth: initScrollDepthEvent(),
  javascriptEvent: initJavascriptEvent(),
  javascriptImportedEvent: initJavascriptImportedEvent(),
};

export const initialUpsellProductTargetingState = (
  checkoutBlockId: string,
  variationId: string,
  priority = 0,
  discountType: UpsellDiscountType | null = null,
  discountValue: number | null = null,
  compareAtPriceDisplay: UpsellCompareAtPriceDisplay = "none"
): { cartTargeting: CartTargetingFe; rule: UpsellProductRulesDto } => {
  const cartTargetingId = v4();

  const cartTargeting: CartTargetingFe = {
    id: cartTargetingId,
    expression: generateDefaultUpsellCartTargetingExpression(),
  };

  const rule: UpsellProductRulesDto = {
    id: v4(),
    checkoutBlockId,
    priority,
    variationId,
    cartTargeting,
    action: generateDefaultUpsellProductRuleAction({
      discountType,
      discountValue,
      compareAtPriceDisplay,
    }),
  };

  return { cartTargeting, rule };
};
